'use client';

import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

interface PromotionBottomBannerItem {
  id: number;
  image: string;
  image_alt?: string;
  jump_content?: any;
}

interface PromotionBottomBannerProps {
  bottomBannerData: PromotionBottomBannerItem[];
  deviceType: number;
  onBannerClickTrack: (item: PromotionBottomBannerItem, index: number) => void;
  onBannerExposureTrack: (item: PromotionBottomBannerItem, index: number) => void;
}

function ExposureDetector({
  item,
  index,
  onExposure,
}: {
  item: PromotionBottomBannerItem;
  index: number;
  onExposure: (item: PromotionBottomBannerItem, index: number) => void;
}) {
  const [ref, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView && item.image) {
      onExposure(item, index);
    }
  }, [inView, index, item, onExposure]);

  return <div ref={ref} className="pointer-events-none absolute inset-0" />;
}

export default function PromotionBottomBanner({
  bottomBannerData,
  deviceType,
  onBannerClickTrack,
  onBannerExposureTrack,
}: PromotionBottomBannerProps) {
  const isWap = deviceType !== EPlatform.WEB;

  if (bottomBannerData.length === 0) {
    return null;
  }

  return (
    <section className={`bg-[#f4f4f4] ${isWap ? 'px-[12px] pb-[32px]' : 'px-[42px] pb-[48px]'}`}>
      <ul className={`grid ${isWap ? 'grid-cols-1 gap-[12px]' : 'grid-cols-2 gap-[12px]'}`}>
        {bottomBannerData.map((item, index) => (
          <li key={item.id || index} className="relative overflow-hidden rounded-[8px]">
            <MagicJump
              item={item.jump_content}
              onBeforeJump={() => onBannerClickTrack(item, index)}
            >
              <Image
                src={item.image}
                alt={item.image_alt || ''}
                width={900}
                height={220}
                className="block h-auto w-full"
              />
            </MagicJump>
            <ExposureDetector item={item} index={index} onExposure={onBannerExposureTrack} />
          </li>
        ))}
      </ul>
    </section>
  );
}
